import { Eye, Package } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import type { InventarioRow } from '../api';

/**
 * Main gallery for the Inventario page. One row per article. Matches
 * the PowerApps Screen_Inventario column set:
 *
 *   Tipo · Modelo · Prestamo/SR · Disponible · Pago Pend. · Pago Parc. ·
 *   Pend. Despacho · Por Ingresar · Stock · Acciones
 *
 * "Pago Pend.", "Pago Parc.", "Pend. Despacho" surface the three
 * committed-sale buckets. The Cotizado bucket is not shown here —
 * PowerApps hides it (cotizado units feel like "not real yet" to
 * admins); it does appear in the detail dialog's bucket strip.
 */
export function InventarioTable({
  rows,
  loading,
  error,
  searchActive,
  onView,
  onRetry,
}: {
  rows: InventarioRow[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onView: (row: InventarioRow) => void;
  onRetry: () => void;
}) {
  const emptyMessage = searchActive
    ? 'Sin coincidencias para los filtros aplicados.'
    : 'Sin artículos en inventario.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Tipo</Th>
              <Th>Artículo</Th>
              <Th className="text-right">Prestamo / SR</Th>
              <Th className="text-right">Disponible</Th>
              <Th className="text-right">Pago Pend.</Th>
              <Th className="text-right">Pago Parc.</Th>
              <Th className="text-right">Pend. Despacho</Th>
              <Th className="text-right">Por Ingresar</Th>
              <Th className="text-right">Stock</Th>
              <Th className="w-16 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={10} />
            ) : error ? (
              <RowStatus cols={10}>
                <span className="text-destructive">
                  No se pudo cargar el inventario.
                </span>
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Reintentar
                </Button>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={10}>
                <Package className="h-5 w-5 text-muted-foreground" aria-hidden />
                {emptyMessage}
              </RowStatus>
            ) : (
              rows.map((r) => (
                <InventarioRowView key={r.id} row={r} onView={() => onView(r)} />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        {loading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-xl" />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-destructive">
            <span>No se pudo cargar el inventario.</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Reintentar
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground">
            <Package className="h-5 w-5" aria-hidden />
            {emptyMessage}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <InventarioCard key={r.id} row={r} onView={() => onView(r)} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function InventarioCard({ row, onView }: { row: InventarioRow; onView: () => void }) {
  const f = row.fields;
  const b = row.buckets;
  return (
    <li
      className="flex flex-col gap-2 px-4 py-3 transition-colors active:bg-muted/40"
      onClick={onView}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView();
        }
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-foreground">
            {f.Articulo_I || row.modelo || '—'}
          </div>
          {row.modelo && f.Articulo_I && row.modelo !== f.Articulo_I && (
            <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
              {row.modelo}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <TipoBadge tipo={f.TipoInventario_I || row.tipoProducto} />
          <DisponiblePill value={b.disponible} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
        {b.prestShow !== 0 && (
          <Stat
            label="Prest./SR"
            value={b.prestShow}
            tone={b.prestShow < 0 ? 'rose' : 'default'}
          />
        )}
        {b.reservado !== 0 && (
          <Stat
            label="Pago Pend."
            value={b.reservado}
            tone={b.reservado < 0 ? 'rose' : 'default'}
          />
        )}
        {b.reservadoParcial !== 0 && (
          <Stat
            label="Pago Parc."
            value={b.reservadoParcial}
            tone={b.reservadoParcial < 0 ? 'rose' : 'default'}
          />
        )}
        {b.pendienteDespacho !== 0 && (
          <Stat
            label="Pend. Despacho"
            value={b.pendienteDespacho}
            tone={b.pendienteDespacho < 0 ? 'rose' : 'violet'}
          />
        )}
        {b.porIngresar !== 0 && (
          <Stat
            label="Por Ingresar"
            value={b.porIngresar}
            tone={b.porIngresar < 0 ? 'rose' : 'sky'}
          />
        )}
        <Stat
          label="Stock"
          value={row.totalStock}
          tone={row.totalStock < 0 ? 'rose' : 'emphasis'}
        />
      </div>
    </li>
  );
}

function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'violet' | 'sky' | 'emphasis' | 'rose';
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide">{label}</div>
      <div
        className={cn(
          'text-sm tabular-nums',
          tone === 'violet' && 'text-violet-700',
          tone === 'sky' && 'text-sky-700',
          tone === 'emphasis' && 'font-semibold text-foreground',
          tone === 'default' && 'text-foreground',
          tone === 'rose' && 'font-semibold text-rose-700',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function InventarioRowView({
  row,
  onView,
}: {
  row: InventarioRow;
  onView: () => void;
}) {
  const f = row.fields;
  const b = row.buckets;

  return (
    <tr className="transition-colors hover:bg-muted/30">
      <Td>
        {/* `TipoInventario_I` sólo se estampa al crear la fila desde una
            recepción; las filas sembradas por catálogo (casi todo MX) lo
            tienen vacío, así que caemos al tipo del artículo. */}
        <TipoBadge tipo={f.TipoInventario_I || row.tipoProducto} />
      </Td>
      <Td>
        <div className="font-medium text-foreground">
          {f.Articulo_I || row.modelo || '—'}
        </div>
        {row.modelo && f.Articulo_I && row.modelo !== f.Articulo_I && (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {row.modelo}
          </div>
        )}
      </Td>
      <Td className="text-right text-sm tabular-nums text-muted-foreground">
        <BucketNumber value={b.prestShow} />
      </Td>
      <Td className="text-right">
        <DisponiblePill value={b.disponible} />
      </Td>
      <Td className="text-right text-sm tabular-nums text-muted-foreground">
        <BucketNumber value={b.reservado} />
      </Td>
      <Td className="text-right text-sm tabular-nums text-muted-foreground">
        <BucketNumber value={b.reservadoParcial} />
      </Td>
      <Td className="text-right text-sm tabular-nums text-muted-foreground">
        <BucketNumber
          value={b.pendienteDespacho}
          positiveClass="bg-violet-100 px-2 py-0.5 text-[12px] font-semibold text-violet-700"
        />
      </Td>
      <Td className="text-right text-sm tabular-nums">
        <BucketNumber
          value={b.porIngresar}
          positiveClass="bg-sky-100 px-2 py-0.5 text-[12px] font-semibold text-sky-700"
        />
      </Td>
      <Td
        className={cn(
          'text-right text-sm font-semibold tabular-nums',
          row.totalStock < 0 && 'text-rose-700',
        )}
      >
        {row.totalStock}
      </Td>
      <Td className="text-right">
        <button
          type="button"
          onClick={onView}
          title="Ver detalle"
          aria-label="Ver detalle"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <Eye className="h-4 w-4" aria-hidden />
        </button>
      </Td>
    </tr>
  );
}

function DisponiblePill({ value }: { value: number }) {
  const cls =
    value > 0
      ? 'bg-emerald-100 text-emerald-700'
      : value < 0
        ? 'bg-rose-100 text-rose-700 ring-1 ring-rose-300'
        : 'bg-rose-100 text-rose-700';
  return (
    <span
      className={cn(
        'inline-flex min-w-10 justify-center rounded-md px-2 py-0.5 text-[13px] font-semibold tabular-nums',
        cls,
      )}
    >
      {value}
    </span>
  );
}

/**
 * Cell renderer for the muted buckets (Prest/SR, Pago Pend., Pago Parc.,
 * Pend. Despacho, Por Ingresar). Positive shows the number (optionally
 * inside a tinted pill via `positiveClass`); zero shows a dash; negative
 * shows the number inside a rose pill so admins can spot the bucket
 * that drifted below zero — that should never happen and surfacing it
 * helps trace which write went wrong.
 */
function BucketNumber({
  value,
  positiveClass,
}: {
  value: number;
  positiveClass?: string;
}) {
  if (value < 0) {
    return (
      <span className="inline-flex rounded-md bg-rose-100 px-2 py-0.5 text-[12px] font-semibold text-rose-700 ring-1 ring-rose-300">
        {value}
      </span>
    );
  }
  if (value > 0) {
    return positiveClass ? (
      <span className={cn('inline-flex rounded-md', positiveClass)}>{value}</span>
    ) : (
      <>{value}</>
    );
  }
  return positiveClass ? <span className="text-muted-foreground">—</span> : <>—</>;
}

function TipoBadge({ tipo }: { tipo: string }) {
  if (!tipo) return <span>—</span>;
  return (
    <span className="inline-flex rounded-md bg-secondary px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-foreground">
      {tipo}
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-3 font-semibold', className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-3 align-top', className)}>{children}</td>;
}

function RowStatus({ children, cols }: { children: React.ReactNode; cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-3 py-3.5">
              <Skeleton className="h-4 w-full max-w-[100px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
